<template>
  <ClientMainLayoutComponent
    :main-layout-ui-model="mainLayoutStore.mainLayoutUiModel"
    :is-user-info-loading="isLoadingCurrentClientUser"
    :are-side-nav-sections-loading="isLoadingCurrentClientUser"
    :is-content-loading="!shouldShowRouterView"
    :can-display-customisation-app-section="canDisplayCustomisationAppSection"
    @logout="presenter.logout()"
    @update:selected-client-id="presenter.changeSelectedClient($event)"
    @navigate="presenter.navigate($event)"
    @open-customisation-app="presenter.openCustomisationAppModal()"
    @open-create-game="presenter.openCreateGameModal()"
    @access-code-copied="presenter.trackAccessCodeCopied()"
  >
    <RouterView />

    <template #side-nav-footer>
      <HelpCenterPopoverComponent />
    </template>

    <template #overlay>
      <CustomisationAppModalFragment v-if="isCustomisationAppModalUiReady" />
      <GameCreationModalFragment v-if="isCreateGameModalUiReady" />
    </template>
  </ClientMainLayoutComponent>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { RouterView, useRoute } from 'vue-router';
import { useCurrentClientUserStore } from '@client/entity/current-client-user/application/store/current-client-user.store';
import { container } from '@shared/common/dependency-injection/domain/container';
import type { MainLayoutPresenter } from '@client/composition/main-layout/domain/presenter/main-layout.presenter';
import { MainLayoutDependencyTypes } from '@client/composition/main-layout/main-layout.dependency-types';
import { useMainLayoutStore } from '@client/composition/main-layout/application/store/main-layout.store';
import { CurrentClientUserDependencyTypes } from '@client/entity/current-client-user/current-client-user.dependency-types';
import type { CurrentClientUserStoreQuery } from '@client/entity/current-client-user/domain/store/current-client-user.store-query';
import ClientMainLayoutComponent from '@client/composition/main-layout/application/layout-component/ClientMainLayoutComponent/ClientMainLayoutComponent.vue';
import HelpCenterPopoverComponent from '@client/feature/help-center/application/component/HelpCenterPopoverComponent/HelpCenterPopoverComponent.vue';

const currentClientUserStore = useCurrentClientUserStore();
const { isLoading: isLoadingCurrentClientUser, selectedClient } = storeToRefs(currentClientUserStore);
const presenter = container.get<MainLayoutPresenter>(MainLayoutDependencyTypes.MainLayoutPresenter);
const currentClientUserStoreQuery = container.get<CurrentClientUserStoreQuery>(
  CurrentClientUserDependencyTypes.CurrentClientUserStoreQuery,
);
const mainLayoutStore = useMainLayoutStore();
const { isCustomisationAppModalUiReady, isCreateGameModalUiReady } = storeToRefs(mainLayoutStore);
const route = useRoute();

const CustomisationAppModalFragment = defineAsyncComponent(
  () =>
    import('@client/feature/customisation-app/application/fragment/CustomisationAppModal/CustomisationAppModalFragment.vue'),
);
const GameCreationModalFragment = defineAsyncComponent(
  () => import('@client/feature/game-creation/application/fragment/GameCreationModal/GameCreationModalFragment.vue'),
);
onMounted(() => {
  presenter.initialize();
});

watch(
  () => route.fullPath,
  () => {
    presenter.updateActiveNavigation(getCurrentRouteName(), route.params);
  },
  { immediate: true },
);

const canDisplayCustomisationAppSection = computed(
  () => currentClientUserStoreQuery.getSelectedClient()?.hasActiveSubscription === true,
);
const shouldShowRouterView = computed(() => selectedClient.value !== null && !isLoadingCurrentClientUser.value);

function getCurrentRouteName(): string | null {
  return typeof route.name === 'string' ? route.name : null;
}
</script>
